import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Table, Tag, Flex, Button, Typography, Tooltip } from "antd";

import PlusIcon from "@/icons/PlusIcon";
import ExclamationCircleIcon from "@/icons/ExclamationCircleIcon";
import LockCloseIcon from "@/icons/LockClosedIcon";
import PencilAltIcon from "@/icons/PencilAltIcon";
import CheckIcon from "@/icons/CheckIcon";
import RefreshIcon from "@/icons/RefreshIcon";

import ContextMenu from "@/components/containers/ContextMenu";
import EmployeeProfile from "@/components/cards/EmployeeProfile";
import CustomLink from "@/components/ui/CustomLink";
import ExpandableText from "@/components/views/ObjectFieldValue/ExpandableText";

import {
  generateRandomKey,
  formateDate,
  formateISODateToMoment,
  getPluralizedWord,
} from "@/utils";
import routes from "@/routes";

import { UserContext } from "@/context/UserContext";
import MainContext from "@/layouts/MainLayout/context";

import { useColumnConfig } from "@/hooks/useColumnConfig";

import {
  PRIORITY_OPTIONS,
  STATUS_OPTIONS,
  EXECUTOR_EDIT_ROLES,
  START_DATE_EDIT_ROLES,
  DUE_DATE_EDIT_ROLES,
  ACCEPTOR_EDIT_ROLES,
  EDIT_PLANNED_LABOR_COSTS_ROLES,
  TASK_TYPE_OPTIONS,
  TASK_FIELD_LABELS,
  isTaskStatusFinal,
} from "@/constants/tasks";

const LOCAL_STORAGE_KEY = "taskTableColumnParameters";

const DEFAULT_COLUMN_ORDER = [
  "task_number",
  "name",
  "readOnly",
  "importance_uuid",
  "due_date",
  "description",
  "executor",
  "status_uuid",
  "is_recurring",
  "start_date",
  "assignee_acceptor",
  "author",
  "sprint_uuid",
  "parent_task_number",
  "parent_task_name",
  "created_at",
  "planned_costs",
  "actual_costs",
  "task_type_uuid",
  "unit",
];
const NON_HIDEABLE_COLUMN_KEYS = [
  "actions",
  "readOnly",
  "is_recurring",
  "check",
];
const FIXED_END_COLUMN_KEYS = ["actions"];

const formateData = (data) => {
  return data.map((item) => {
    return {
      ...item,
      key: generateRandomKey(),
      // ...(item.has_children && { children: [] }),
    };
  });
};

const toFloorNumber = (value, precision = 0) => {
  if (value === null || value === undefined || value === "") return null;

  const num = Number(String(value).replace(",", "."));
  if (!Number.isFinite(num)) return null;

  if (precision <= 0) {
    return Math.floor(num);
  }

  const factor = 10 ** precision;
  return Math.floor(num * factor) / factor;
};

// Таблица задач
// Содержит список с возможностью сортировки
// Тут можно настроить колонки и ContentMenu
const TasksTable = ({
  data,
  loading,
  pagination,
  statuses = [],
  priorities = [],
  onChange,
  onCopyItem,
  onEditItem,
  onDeleteItem,
  onPublishItem,
  onAddActualCosts,
  onEditPlannedCosts,
  onRemoveTaskFromSprint,
  unit = null,
  isAdmins = false,
  scroll = { x: "100%" },
  resize = false,
  selectedRowKeys,
  onRowsSelect,
}) => {
  const navigate = useNavigate();

  const { user } = useContext(UserContext);
  const { config } = useContext(MainContext);

  const sprintLabel = config?.entities?.sprint?.name?.singular || "Спринт";

  const columnLabels = useMemo(
    () => ({
      ...TASK_FIELD_LABELS,
      sprint_uuid: `${sprintLabel}`,
      readOnly: "Только чтение",
      is_recurring: "Повторяющаяся задача",
    }),
    [sprintLabel],
  );

  const getTaskStatus = useCallback(
    (task) =>
      statuses.find(({ uuid }) => uuid === task?.status_uuid) || task?.status,
    [statuses],
  );

  const isTaskFinal = useCallback(
    (task) => isTaskStatusFinal(getTaskStatus(task)),
    [getTaskStatus],
  );

  const [dataSource, setDataSource] = useState([]);

  useEffect(() => {
    setDataSource(formateData(data));
  }, [data]);

  const baseColumns = useMemo(() => {
    const getHref = (item, unit_uuid) => {
      return isAdmins
        ? routes.administration.taskDetail(item)
        : unit
          ? routes.unitTaskDetail(unit_uuid, item)
          : routes.taskDetail(item);
    };
    return [
      {
        title: columnLabels.task_number,
        dataIndex: "task_number",
        key: "task_number",
        width: 60,
        fixed: "left",
      },
      {
        title: columnLabels.name,
        dataIndex: "name",
        key: "name",
        width: 300,
        fixed: "left",
        render: (name, record) => (
          <CustomLink
            to={getHref(record.uuid, record.unit_uuid)}
            color="#303030"
          >
            <Tooltip placement="bottomLeft" title={name}>
              <Typography.Paragraph
                ellipsis={{ rows: 2 }}
                style={{
                  color: "inherit",
                  lineHeight: 1.4,
                  marginBottom: 0,
                  display: "-webkit-inline-box",
                }}
              >
                {name}
              </Typography.Paragraph>
            </Tooltip>
          </CustomLink>
        ),
      },
      {
        title: <PencilAltIcon style={{ fontSize: 14, color: "#BBBBBB" }} />,
        dataIndex: "readOnly",
        key: "readOnly",
        width: 50,
        align: "center",
        render: (readOnly) =>
          readOnly ? (
            <LockCloseIcon
              style={{
                fontSize: 18,
                color: "#BBBBBB",
                verticalAlign: "middle",
              }}
            />
          ) : (
            <CheckIcon
              style={{
                fontSize: 18,
                color: "#BBBBBB",
                verticalAlign: "middle",
              }}
            />
          ),
      },
      {
        title: columnLabels.created_at,
        width: 150,
        dataIndex: "created_at",
        key: "created_at",
        render: (created_at, task) => {
          return (
            <Flex align="center" gap={4}>
              <span>{formateISODateToMoment(created_at)}</span>
            </Flex>
          );
        },
      },
      {
        title: columnLabels.importance_uuid,
        width: 110,
        dataIndex: "importance_uuid",
        key: "importance_uuid",
        render: (importance_uuid) => {
          const priority = priorities.find(
            (priority) => priority.uuid === importance_uuid,
          );
          const icon = PRIORITY_OPTIONS.find(
            (item) => item.value === priority.color,
          )?.icon;
          return (
            <Tag icon={icon} className="tag-with-icon">
              {priority.name}
            </Tag>
          );
        },
      },
      {
        title: columnLabels.status_uuid,
        width: 150,
        dataIndex: "status_uuid",
        key: "status_uuid",
        render: (status_uuid) => {
          if (!status_uuid) return;

          const status = statuses.find((status) => status.uuid === status_uuid);
          const icon = STATUS_OPTIONS.find(
            (item) => item.value === status.color,
          )?.icon;
          return (
            <Tag
              icon={icon}
              color={status.color}
              className="tag-with-icon-color"
            >
              {status.name}
            </Tag>
          );
        },
      },
      {
        title: <RefreshIcon style={{ fontSize: 14, color: "#BBBBBB" }} />,
        dataIndex: "is_recurring",
        key: "is_recurring",
        width: 50,
        align: "center",
        render: (is_recurring) =>
          is_recurring && (
            <RefreshIcon style={{ fontSize: 14, color: "#BBBBBB" }} />
          ),
      },
      {
        title: columnLabels.description,
        dataIndex: "description",
        key: "description",
        render: (value) => <ExpandableText value={value} line={3} markdown />,
        width: 200,
      },
      {
        title: columnLabels.task_type_uuid,
        width: 150,
        dataIndex: "task_type_uuid",
        key: "task_type_uuid",
        render: (_, record) => {
          const taskType = record.type || {};
          const icon = TASK_TYPE_OPTIONS.find(
            (item) => item.value === taskType.style_key,
          )?.icon;
          return (
            <Tag icon={icon} className="tag-with-icon lg">
              {taskType.name}
            </Tag>
          );
        },
      },
      {
        title: columnLabels.sprint_uuid,
        width: 150,
        dataIndex: "sprint_uuid",
        key: "sprint_uuid",
        render: (_, record) => {
          const sprint = record.sprint;

          if (!sprint) return;

          return (
            <CustomLink
              to={
                isAdmins
                  ? routes.administration.sprintDetail(sprint.uuid)
                  : routes.sprintDetail(sprint.uuid)
              }
              color="#303030"
            >
              <Tooltip placement="bottomLeft" title={sprint.name}>
                <Typography.Paragraph
                  ellipsis={{ rows: 2 }}
                  style={{
                    color: "inherit",
                    lineHeight: 1.4,
                    marginBottom: 0,
                    display: "-webkit-inline-box",
                  }}
                >
                  {sprint.name}
                </Typography.Paragraph>
              </Tooltip>
            </CustomLink>
          );
        },
      },
      {
        title: columnLabels.author,
        dataIndex: "author",
        key: "author",
        width: 180,
        ellipsis: { showTitle: false },
        render: (author, item) => {
          return (
            <EmployeeProfile
              {...author}
              fullName={false}
              avatarSize={24}
              tooltip
            />
          );
        },
      },
      {
        title: columnLabels.executor,
        dataIndex: "executor",
        key: "executor",
        width: 180,
        ellipsis: { showTitle: false },
        render: (executor, item) => {
          const isFinal = isTaskFinal(item);

          const isUserCanEditExecutor = item.roles
            ? item.roles.some((role) => EXECUTOR_EDIT_ROLES.includes(role))
            : true;

          if (!executor) {
            return (
              !isFinal &&
              !item.readOnly &&
              isUserCanEditExecutor && (
                <Button
                  color="default"
                  size="small"
                  variant="link"
                  type="text"
                  icon={<PlusIcon />}
                  onClick={() => onEditItem(item)}
                  style={{ paddingInline: 0 }}
                >
                  исполнитель
                </Button>
              )
            );
          }

          return (
            <EmployeeProfile
              {...executor}
              fullName={false}
              avatarSize={24}
              tooltip
            />
          );
        },
      },
      {
        title: columnLabels.assignee_acceptor,
        dataIndex: "assignee_acceptor",
        key: "assignee_acceptor",
        width: 180,
        ellipsis: { showTitle: false },
        render: (assignee_acceptor, item) => {
          const isFinal = isTaskFinal(item);

          const isUserCanEditAcceptor = item.roles
            ? item.roles.some((role) => ACCEPTOR_EDIT_ROLES.includes(role))
            : true;

          if (!assignee_acceptor) {
            return (
              !isFinal &&
              !item.readOnly &&
              isUserCanEditAcceptor && (
                <Button
                  color="default"
                  size="small"
                  variant="link"
                  type="text"
                  icon={<PlusIcon />}
                  onClick={() => onEditItem(item)}
                  style={{ paddingInline: 0 }}
                >
                  приемщик
                </Button>
              )
            );
          }

          return (
            <EmployeeProfile
              {...assignee_acceptor}
              fullName={false}
              avatarSize={24}
              tooltip
            />
          );
        },
      },
      {
        title: columnLabels.parent_task_number,
        dataIndex: "parent",
        key: "parent_task_number",
        width: 120,
        render: (parent, record) =>
          parent?.task_number &&
          record.parent_task_uuid && <>{parent.task_number}</>,
      },
      {
        title: columnLabels.parent_task_name,
        dataIndex: "parent",
        key: "parent_task_name",
        width: 250,
        render: (parent, record) =>
          parent?.task_number &&
          record.parent_task_uuid && (
            <CustomLink
              to={getHref(record.parent_task_uuid, record.parent.unit_uuid)}
              color="#303030"
            >
              <Tooltip placement="bottomLeft" title={parent.name}>
                <Typography.Paragraph
                  ellipsis={{ rows: 2 }}
                  style={{
                    color: "inherit",
                    lineHeight: 1.4,
                    marginBottom: 0,
                    display: "-webkit-inline-box",
                  }}
                >
                  {parent.name}
                </Typography.Paragraph>
              </Tooltip>
            </CustomLink>
          ),
      },
      {
        title: columnLabels.start_date,
        width: 110,
        dataIndex: "start_date",
        key: "start_date",
        render: (start_date, task) => {
          const isFinal = isTaskFinal(task);

          const isUserCanEditStartDate = task.roles
            ? task.roles.some((role) => START_DATE_EDIT_ROLES.includes(role))
            : true;

          return start_date
            ? formateDate(start_date)
            : !isFinal &&
                !task.readOnly &&
                isUserCanEditStartDate && (
                  <Button
                    color="default"
                    variant="link"
                    size="small"
                    type="text"
                    icon={<PlusIcon />}
                    onClick={() => onEditItem(task)}
                    style={{ paddingInline: 0 }}
                  >
                    дата
                  </Button>
                );
        },
      },
      {
        title: columnLabels.due_date,
        width: 150,
        dataIndex: "due_date",
        key: "due_date",
        render: (due_date, task) => {
          const isFinal = isTaskFinal(task);

          const is_overdue = due_date && new Date() >= new Date(due_date);

          const isUserCanEditDueDate = task.roles
            ? task.roles.some((role) => DUE_DATE_EDIT_ROLES.includes(role))
            : true;

          return due_date ? (
            <Flex align="center" gap={4}>
              {is_overdue && (
                <ExclamationCircleIcon
                  style={{ fontSize: 14, color: "#E66A25" }}
                />
              )}
              <span>{formateISODateToMoment(due_date)}</span>
            </Flex>
          ) : (
            !isFinal &&
              !task.readOnly &&
              isUserCanEditDueDate && (
                <Button
                  color="default"
                  variant="link"
                  size="small"
                  type="text"
                  icon={<PlusIcon />}
                  onClick={() => onEditItem(task)}
                  style={{ paddingInline: 0 }}
                >
                  срок
                </Button>
              )
          );
        },
      },
      {
        title: columnLabels.planned_costs,
        width: 150,
        dataIndex: "planned_costs",
        key: "planned_costs",
        ellipsis: { showTitle: false },
        render: (costs, task) => {
          const planned = costs ? Number(costs) : null;
          const draftStatus = statuses.find(({ is_for_draft }) => is_for_draft);
          const isDraft = task?.status_uuid === draftStatus?.uuid;
          const isFinal = isTaskFinal(task);

          const isUserCanEditPlannedCosts = task.roles
            ? task.roles.some((role) =>
                EDIT_PLANNED_LABOR_COSTS_ROLES.includes(role),
              )
            : true;

          return planned ? (
            <Tooltip
              placement="bottomLeft"
              title={`${planned} ${getPluralizedWord(planned, [
                "час",
                "часа",
                "часов",
              ])}`}
            >
              {`${planned} ${getPluralizedWord(toFloorNumber(planned), [
                "час",
                "часа",
                "часов",
              ])}`}
            </Tooltip>
          ) : (
            !isFinal &&
              !isDraft &&
              !task.readOnly &&
              isUserCanEditPlannedCosts && (
                <Button
                  color="default"
                  variant="link"
                  size="small"
                  type="text"
                  icon={<PlusIcon />}
                  onClick={() => onEditPlannedCosts(task)}
                  style={{ paddingInline: 0 }}
                >
                  трудозатраты
                </Button>
              )
          );
        },
      },
      {
        title: columnLabels.actual_costs,
        width: 150,
        dataIndex: "actual_costs",
        key: "actual_costs",
        ellipsis: { showTitle: false },
        render: (costs, task) => {
          const actual = Number(costs) || 0;
          const childrenActual = Number(task.sum_children_actual_costs) || 0;

          const total = actual + childrenActual;

          const draftStatus = statuses.find(({ is_for_draft }) => is_for_draft);
          const isDraft = task?.status_uuid === draftStatus?.uuid;
          const isFinal = isTaskFinal(task);

          return total > 0 ? (
            <Tooltip
              placement="bottomLeft"
              title={`${total} ${getPluralizedWord(total, [
                "час",
                "часа",
                "часов",
              ])}`}
            >
              {`${toFloorNumber(total)} ${getPluralizedWord(
                toFloorNumber(total),
                ["час", "часа", "часов"],
              )}`}
            </Tooltip>
          ) : (
            !isFinal && !isDraft && !task.readOnly && (
              <Button
                color="default"
                variant="link"
                size="small"
                type="text"
                icon={<PlusIcon />}
                onClick={() => onAddActualCosts(task)}
                style={{ paddingInline: 0 }}
              >
                трудозатраты
              </Button>
            )
          );
        },
      },
      {
        title: columnLabels.unit,
        width: 150,
        dataIndex: "unit",
        key: "unit",
        ellipsis: { showTitle: false },
        render: (unit) => (
          <Tooltip placement="bottomLeft" title={unit?.name}>
            {unit?.name}
          </Tooltip>
        ),
      },
      {
        title: "",
        width: 48,
        align: "center",
        fixed: "right",
        key: "actions",
        render: (_, item) => {
          const draftStatus = statuses.find(({ is_for_draft }) => is_for_draft);

          const isFinal = isTaskFinal(item);
          const isDraft = item?.status_uuid === draftStatus?.uuid;

          return item.readOnly ? (
            // <LockCloseIcon style={{ color: "#BBBBBB", fontSize: 18 }} />
            <ContextMenu
              vertical
              onCopy={() => onCopyItem(item)}
              onView={() => navigate(getHref(item.uuid, item.unit_uuid))}
            />
          ) : isFinal ? (
            <ContextMenu
              vertical
              onCopy={() => onCopyItem(item)}
              onView={() => navigate(getHref(item.uuid, item.unit_uuid))}
              onRemoveFromSprint={
                onRemoveTaskFromSprint
                  ? () => onRemoveTaskFromSprint(item)
                  : null
              }
              removeFromSprintText={`Удалить из ${item.sprint?.name}`}
            />
          ) : isDraft ? (
            <ContextMenu
              vertical
              onCopy={() => onCopyItem(item)}
              onView={() => navigate(getHref(item.uuid, item.unit_uuid))}
              onEdit={() => onEditItem(item)}
              onDelete={() => onDeleteItem(item)}
              onPublish={() => onPublishItem(item)}
              onRemoveFromSprint={
                onRemoveTaskFromSprint
                  ? () => onRemoveTaskFromSprint(item)
                  : null
              }
              removeFromSprintText={`Удалить из ${item.sprint?.name}`}
            />
          ) : (
            <ContextMenu
              vertical
              onCopy={() => onCopyItem(item)}
              onView={() => navigate(getHref(item.uuid, item.unit_uuid))}
              onEdit={() => onEditItem(item)}
              onDelete={() => onDeleteItem(item)}
              onRemoveFromSprint={
                onRemoveTaskFromSprint
                  ? () => onRemoveTaskFromSprint(item)
                  : null
              }
              removeFromSprintText={`Удалить из ${item.sprint?.name}`}
            />
          );
        },
      },
    ].filter(Boolean);
  }, [
    priorities,
    statuses,
    navigate,
    onEditItem,
    onDeleteItem,
    onPublishItem,
    onCopyItem,
    onRemoveTaskFromSprint,
    onEditPlannedCosts,
    isAdmins,
    unit,
    onAddActualCosts,
    columnLabels,
    isTaskFinal,
  ]);

  const { columns, components } = useColumnConfig(
    baseColumns,
    user.user_uuid,
    LOCAL_STORAGE_KEY,
    {
      resize,
      columnLabels,
      reorder: true,
      defaultOrder: DEFAULT_COLUMN_ORDER,
      excludeKeys: NON_HIDEABLE_COLUMN_KEYS,
      fixedEndKeys: FIXED_END_COLUMN_KEYS,
    },
  );

  return (
    <Table
      dataSource={dataSource}
      columns={columns}
      rowKey="uuid"
      loading={loading}
      pagination={pagination}
      onChange={onChange}
      rowSelection={
        onRowsSelect
          ? {
              selectedRowKeys,
              preserveSelectedRowKeys: true,
              fixed: true,
              onChange: onRowsSelect,
            }
          : undefined
      }
      components={resize ? components : undefined}
      showSorterTooltip={{
        target: "sorter-icon",
        title: "Нажмите для сортировки",
      }}
      scroll={scroll}
      tableLayout="fixed"
      bordered
      // rowClassName={(record) => (record.readOnly ? "row-read-only" : "")}
    />
  );
};

export default TasksTable;
